import React from "react";
import { Helmet } from "react-helmet-async";
import { trackEvent, EventNames } from "../analytics";
import { canonicalUrl } from "../siteConfig";

export default function SeoRevenueLeakAuditPage() {
  const trackAssessmentCta = (source) => trackEvent(EventNames.AssessmentCtaClicked, { source });

  return (
    <div className="app page-grid">
      <Helmet>
        <title>Technical SEO & Visibility Assessment | OptiSiteSEO</title>
        <meta
          name="description"
          content="Turn a Free SEO Scan into a Technical SEO & Visibility Assessment with prioritized discoverability, indexing, crawlability, performance, AI discoverability, analytics, measurement, and implementation findings."
        />
        <link rel="canonical" href={canonicalUrl("/seo-revenue-leak-audit/index.html")} />
        <meta property="og:title" content="Technical SEO & Visibility Assessment | OptiSiteSEO" />
        <meta
          property="og:description"
          content="Starter $297, Growth $697, Advanced $1,500. Built for businesses that need a technical SEO implementation roadmap."
        />
        <meta property="og:url" content={canonicalUrl("/seo-revenue-leak-audit/index.html")} />
      </Helmet>

      <nav className="top-nav">
        <div className="brand">
          <span>OptiSiteSEO</span>
        </div>
        <div className="nav-links">
          <a className="link" href="/">
            Home
          </a>
          <a className="link" href="/demo/index.html">
            Sample report
          </a>
          <a className="link" href="/what-we-check/index.html">
            What we check
          </a>
          <a className="primary" href="/app/index.html" onClick={() => trackEvent(EventNames.CtaClicked, { cta_id: "audit-page-free-scan" })}>
            Run Free SEO Scan
          </a>
        </div>
      </nav>

      <header className="service-hero audit-hero">
        <div className="service-hero-copy">
          <p className="eyebrow">Technical SEO & Visibility Assessment after the Free SEO Scan</p>
          <h1>Technical SEO & Visibility Assessment</h1>
          <p className="subtext">
            For businesses with technical SEO issues, discoverability blockers, and unclear measurement. Start with the Free SEO Scan, then request a
            manual assessment that turns indexing, crawlability, performance, AI discoverability, analytics, and measurement gaps into a prioritized
            implementation roadmap.
          </p>
          <div className="hero-actions">
            <a className="primary" href="/app/index.html" onClick={() => trackEvent(EventNames.CtaClicked, { cta_id: "audit-page-primary-scan" })}>
              Run Free SEO Scan
            </a>
            <a className="secondary" href="/request-assessment" onClick={() => trackAssessmentCta("legacy_audit_hero")}>
              Request Assessment
            </a>
          </div>
          <div className="badge-row">
            <span className="badge">Starter $297</span>
            <span className="badge">Growth $697</span>
            <span className="badge">Advanced $1,500</span>
            <span className="badge">No outcome guarantees</span>
          </div>
        </div>
        <aside className="offer-summary" aria-label="Assessment scope summary">
          <p className="eyebrow">What the assessment adds</p>
          <h2>A prioritized implementation roadmap</h2>
          <p>
            The Free SEO Scan shows the signals. The Technical SEO & Visibility Assessment adds manual review, business context, and a clear order of
            work for technical visibility fixes.
          </p>
          <div className="offer-facts">
            <div>
              <span className="label">Intro price</span>
              <strong>$297-$1,500</strong>
            </div>
            <div>
              <span className="label">Scope</span>
              <strong>Confirmed after intake</strong>
            </div>
            <div>
              <span className="label">Delivery</span>
              <strong>2-5 business days</strong>
            </div>
          </div>
        </aside>
      </header>

      <section className="info-columns section" aria-label="Assessment review areas">
        <div className="plain-panel">
          <p className="eyebrow">Visibility problems</p>
          <h2>Can the site be found and understood?</h2>
          <ul className="bullets">
            <li>Indexing signals, crawl blockers, redirects, canonicals, and sitemap basics</li>
            <li>Metadata, headings, page structure, mobile issues, and speed risk signals</li>
            <li>Priority pages that may be unclear to search engines or customers</li>
          </ul>
        </div>
        <div className="plain-panel">
          <p className="eyebrow">Growth bottlenecks</p>
          <h2>Can visitors become leads?</h2>
          <ul className="bullets">
            <li>Weak CTAs, unclear next steps, and missing conversion paths</li>
            <li>Thin service pages, weak local presence, and missing location or offer coverage</li>
            <li>Pages where unclear structure or missing measurement can hide what is working</li>
          </ul>
        </div>
        <div className="plain-panel">
          <p className="eyebrow">Prioritized action</p>
          <h2>What should be fixed first?</h2>
          <ul className="bullets">
            <li>Issues prioritized by likely impact, effort, and dependency order</li>
            <li>Recommended fixes written for a site owner, marketer, or developer</li>
            <li>A first-next-later action plan instead of a flat checklist</li>
          </ul>
        </div>
      </section>

      <section className="paid-offer-band section">
        <div className="section-header">
          <div>
          <p className="eyebrow">Deliverable</p>
          <h2>What you receive</h2>
          </div>
        </div>
        <ul className="bullets">
          <li>A manual review of the Free SEO Scan result, priority public pages, and intake details</li>
          <li>A prioritized issue list covering technical SEO, discoverability, crawlability, indexability, structured data, performance, analytics, and measurement</li>
          <li>Recommended fixes with enough context to hand to a site owner, marketer, or developer</li>
          <li>Scope notes that separate quick wins, larger fixes, and items that need more discovery</li>
        </ul>
      </section>

      <section className="card section compact-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Scope boundaries</p>
            <h2>What this assessment is not</h2>
          </div>
        </div>
        <ul className="bullets">
          <li>It does not guarantee traffic, leads, revenue, or discovery outcomes.</li>
          <li>It does not include backlink campaigns, broad keyword strategy, content marketing retainers, paid ads management, implementation, or ongoing reporting.</li>
          <li>It reviews public pages and intake context. Login-only areas, private analytics, and large content inventories require separate scope.</li>
          <li>The Free SEO Scan lets you see the report format before requesting paid work.</li>
        </ul>
      </section>

      <section className="paid-offer-band section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Buyer path</p>
            <h2>Free SEO Scan first. Technical SEO & Visibility Assessment when you need priorities.</h2>
          </div>
        </div>
        <p>
          Run the Free SEO Scan on one public URL. After the result or demo report is shown, use the assessment request form to send your intake details. If
          the site needs a deeper Technical SEO & Visibility Assessment, scope and price are confirmed before paid work starts.
        </p>
        <div className="cta-row">
          <a className="primary" href="/app/index.html" onClick={() => trackEvent(EventNames.CtaClicked, { cta_id: "audit-page-bottom-scan" })}>
            Run Free SEO Scan
          </a>
          <a className="secondary" href="/request-assessment" onClick={() => trackAssessmentCta("legacy_audit_bottom")}>
            Request Assessment
          </a>
        </div>
      </section>
    </div>
  );
}
